import { useState } from "react";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as UserIcon } from "../../../assets/user.icon.svg";
import { refreshContentItemProvider } from "../../../api/admin/content-providers/endpoints-referrers";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { LoaderStep } from "../../../common/enums/loader-step.enum";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { Provider } from "../../../common/interfaces/admin/provider.interface";
import { Loader } from "../../loader/loader.comp";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import { useGoogleAuthPopup } from "../../../common/utilities/google-auth/useGoogleAuthPopup";
import { useOneDriveAuthPopup } from "../../../common/utilities/onedrive-auth/useOneDriveAuthPopup";

export const ProviderRefreshModal = ({
    open,
    provider,
    handleClose,
    handleAction
}: {
    open: boolean;
    provider: Provider;
    handleClose: () => void;
    handleAction: () => void;
}) => {
    const { user } = FirebaseAuthContainer.useContainer();
    const [step, setStep] = useState<LoaderStep>(LoaderStep.start);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isGoogle] = useState(provider.name === ProviderType.googleDrive);
    const descriptionMessage = `To refresh your source you have to be signed in to ${isGoogle ? "Google" : "Microsoft"} with the same email${provider.email ? ":" : ""}
                                ${provider.email ?? "you used to connect this drive"}`;

    // Handle refresh action after successful authentication
    const handleRefreshAction = (accessToken: string) => {
        if (!user?.currentKnowledgeBaseId) return;

        setStep(LoaderStep.loading);
        const params = isGoogle
            ? {
                  gat: accessToken,
                  knowledgeBaseId: user.currentKnowledgeBaseId
              }
            : {
                  oneAccessToken: accessToken,
                  knowledgeBaseId: user.currentKnowledgeBaseId
              };
        refreshContentItemProvider(user.companyId, provider.id, params)
            .then(() => {
                setStep(LoaderStep.complete);
                handleAction();
            })
            .catch(error => {
                // 409: an import is already scanning/creating for this source —
                // show the server's message, not the permissions hint.
                if (error?.response?.status === 409) {
                    setErrorMessage(
                        typeof error.response.data?.message === "string"
                            ? error.response.data.message
                            : "An import is already running for this source."
                    );
                    setStep(LoaderStep.error);
                    return;
                }
                switch (error.code) {
                    case "ERR_NETWORK":
                        setErrorMessage("Network error.");
                        break;
                    case "ERR_BAD_REQUEST":
                        setErrorMessage(`There was a problem refreshing ${isGoogle ? "Google" : "One"} Drive "${provider.displayName}".
                                Check its permissions for external access.`);
                        break;
                    default:
                        setErrorMessage("Something went wrong. Please try again later.");
                }
                setStep(LoaderStep.error);
            });
    };

    // Google Auth popup hook (only enabled for Google Drive providers)
    const { openAuth: openGoogleAuth } = useGoogleAuthPopup({
        expectedEmail: provider.email || undefined,
        onSuccess: accessToken => {
            handleRefreshAction(accessToken);
        },
        onCancel: () => {
            // User cancelled, do nothing
        },
        onError: error => {
            console.error("Google auth error:", error);
            setErrorMessage(error);
            setStep(LoaderStep.error);
        },
        enabled: isGoogle
    });

    // OneDrive Auth popup hook (only enabled for OneDrive providers)
    const { openAuth: openOneDriveAuth } = useOneDriveAuthPopup({
        expectedEmail: provider.email || undefined,
        onSuccess: accessToken => {
            handleRefreshAction(accessToken);
        },
        onCancel: () => {
            // User cancelled, do nothing
        },
        onError: error => {
            console.error("OneDrive auth error:", error);
            setErrorMessage(error);
            setStep(LoaderStep.error);
        },
        enabled: !isGoogle
    });

    const handleCloseClick = () => {
        handleClose();
    };

    const handleActionClick = () => {
        if (isGoogle) {
            openGoogleAuth();
        } else {
            openOneDriveAuth();
        }
    };

    return (
        <ModalTemplate
            open={open}
            width={654}
            handleClose={handleCloseClick}
            ariaLabel="Refresh source"
        >
            <div className="provider-refresh-view">
                {(step === LoaderStep.start || step === LoaderStep.loading) && (
                    <>
                        <div className="provider-refresh-title">
                            {step === LoaderStep.start ? (
                                <SvgIcon
                                    className="provider-refresh-title__icon"
                                    component={UserIcon}
                                    inheritViewBox
                                />
                            ) : (
                                <Loader size={48} borderWidth={5} />
                            )}
                            <span className="provider-refresh-title__text">Authenticate</span>
                        </div>
                        <div className="provider-refresh-description">
                            <span className="provider-refresh-description__text">
                                {descriptionMessage}
                            </span>
                        </div>
                    </>
                )}
                {step === LoaderStep.complete && (
                    <>
                        <div className="provider-refresh-title provider-refresh-title--complete">
                            <SvgIcon
                                className="provider-refresh-title__icon"
                                component={UserIcon}
                                inheritViewBox
                            />
                            <span className="provider-refresh-title__text">
                                {isGoogle ? "Google" : "One"} Drive Refreshed
                            </span>
                        </div>
                        <div className="provider-refresh-description">
                            <span className="provider-refresh-description__text">
                                The refresh may take some time depending on the size and number of
                                files.
                            </span>
                        </div>
                    </>
                )}
                {step === LoaderStep.error && (
                    <>
                        <div className="provider-refresh-title provider-refresh-title--error">
                            <SvgIcon
                                className="provider-refresh-title__icon"
                                component={UserIcon}
                                inheritViewBox
                            />
                            <span className="provider-refresh-title__text">Refresh Error</span>
                        </div>
                        <div className="provider-refresh-description">
                            <span className="provider-refresh-description__text">
                                {errorMessage}
                            </span>
                        </div>
                    </>
                )}
                <div className="provider-refresh-action">
                    {(step === LoaderStep.start || step === LoaderStep.loading) && (
                        <>
                            <Button
                                className="thirt-button"
                                variant="text"
                                onClick={handleCloseClick}
                                disabled={step === LoaderStep.loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="primary-button button-left-margin"
                                variant="text"
                                onClick={handleActionClick}
                                disabled={step === LoaderStep.loading}
                            >
                                {step === LoaderStep.start
                                    ? `Authenticate in ${isGoogle ? "Google" : "One Drive"}`
                                    : "Please wait"}
                            </Button>
                        </>
                    )}
                    {(step === LoaderStep.complete || step === LoaderStep.error) && (
                        <Button
                            variant="text"
                            className="primary-button"
                            onClick={handleCloseClick}
                        >
                            Close
                        </Button>
                    )}
                </div>
            </div>
        </ModalTemplate>
    );
};
